import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import axios from "axios";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { formatDateLong } from "../utils/dateUtils";
import {
  Calendar,
  Clock,
  MapPin,
  ArrowLeft,
  Loader2,
  CheckCircle2,
  User,
  Phone,
  Mail,
  CreditCard,
  Music,
  Heart,
  Brain,
  GraduationCap,
  Dumbbell,
  HandHeart,
  Users2,
  ClipboardList,
  KeyRound,
  UserCheck,
  Baby,
  Plus,
  X
} from "lucide-react";

const TYPE_THEME = {
  evento: { icon: Calendar, label: "Evento", bg: "bg-amber-500", soft: "from-amber-50 to-orange-100", ring: "ring-amber-200", text: "text-amber-700" },
  evento_dupla: { icon: Users2, label: "Evento em Dupla", bg: "bg-pink-500", soft: "from-pink-50 to-rose-100", ring: "ring-pink-200", text: "text-pink-700" },
  inscricao: { icon: ClipboardList, label: "Inscrição", bg: "bg-teal-500", soft: "from-teal-50 to-cyan-100", ring: "ring-teal-200", text: "text-teal-700" },
  musica: { icon: Music, label: "Música", bg: "bg-purple-500", soft: "from-purple-50 to-violet-100", ring: "ring-purple-200", text: "text-purple-700" },
  doacoes: { icon: Heart, label: "Doações", bg: "bg-red-500", soft: "from-red-50 to-rose-100", ring: "ring-red-200", text: "text-red-700" },
  terapias: { icon: Brain, label: "Terapias", bg: "bg-blue-500", soft: "from-blue-50 to-sky-100", ring: "ring-blue-200", text: "text-blue-700" },
  cursos: { icon: GraduationCap, label: "Cursos", bg: "bg-green-500", soft: "from-green-50 to-emerald-100", ring: "ring-green-200", text: "text-green-700" },
  esporte: { icon: Dumbbell, label: "Esporte", bg: "bg-orange-500", soft: "from-orange-50 to-amber-100", ring: "ring-orange-200", text: "text-orange-700" },
  oracao: { icon: HandHeart, label: "Oração", bg: "bg-indigo-500", soft: "from-indigo-50 to-blue-100", ring: "ring-indigo-200", text: "text-indigo-700" },
  evento_infantil: { icon: Baby, label: "Evento Infantil", bg: "bg-yellow-500", soft: "from-yellow-50 to-amber-100", ring: "ring-yellow-200", text: "text-yellow-700" },
};
const getTheme = (tipo) => TYPE_THEME[tipo] || TYPE_THEME.inscricao;

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FORMAS_PAGAMENTO = [
  { value: "pix", label: "PIX" },
  { value: "dinheiro", label: "Dinheiro" },
  { value: "cartao_credito", label: "Cartão de Crédito" },
  { value: "cartao_debito", label: "Cartão de Débito" },
  { value: "transferencia", label: "Transferência Bancária" },
];

export default function PublicEnrollPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [acesso, setAcesso] = useState(null);
  const [lookupState, setLookupState] = useState("idle"); // idle | loading | found | notfound
  const [prefill, setPrefill] = useState({});
  const [criancas, setCriancas] = useState([{ nome: "", idade: "" }]);
  const [project, setProject] = useState(null);
  const isInfantil = project?.tipo === "evento_infantil";
  const [churchInfo, setChurchInfo] = useState({});
  
  const [form, setForm] = useState({
    nome_completo: "",
    telefone: "",
    email: "",
    data_nascimento: "",
    endereco: "",
    bairro: "",
    cidade: "",
    estado: "",
    forma_pagamento: "",
    data_pagamento: "",
    observacoes: "",
    sexo: "",
    estado_civil: "",
    situacao: "",
    batismo: "",
    ministerios_desejados: "",
    convidado_nome: "",
    convidado_telefone: "",
    convidado_email: ""
  });
  const [ministries, setMinistries] = useState([]);

  const DEFAULT_FIELDS = ["email", "data_nascimento", "endereco", "bairro", "cidade"];
  const configured = project?.campos_inscricao_externa?.length > 0 ? project.campos_inscricao_externa : DEFAULT_FIELDS;
  const has = (f) => configured.includes(f);
  const theme = getTheme(project?.tipo);
  const TypeIcon = theme.icon;

  // Busca cadastro pelo telefone e pré-preenche
  useEffect(() => {
    const digits = (form.telefone || "").replace(/\D/g, "");
    if (digits.length < 10) { setLookupState("idle"); return; }
    const t = setTimeout(async () => {
      setLookupState("loading");
      // limpa o que foi pré-preenchido pela busca anterior
      setForm(prev => {
        const cleared = { ...prev };
        Object.entries(prefill).forEach(([k, v]) => { if (cleared[k] === v) cleared[k] = ""; });
        return cleared;
      });
      try {
        const res = await axios.get(`${API}/public/people/lookup`, { params: { telefone: digits } });
        if (res.data?.found) {
          const p = res.data.pessoa;
          const filled = {
            nome_completo: p.nome_completo || "", email: p.email || "",
            data_nascimento: p.data_nascimento ? p.data_nascimento.split("T")[0] : "",
            sexo: p.sexo || "", endereco: p.endereco || "", bairro: p.bairro || "", cidade: p.cidade || "",
            estado_civil: p.estado_civil || "", situacao: p.situacao || "", batismo: p.batismo || "",
          };
          setPrefill(filled);
          setForm(prev => ({
            ...prev,
            nome_completo: prev.nome_completo || p.nome_completo || "",
            email: prev.email || p.email || "",
            data_nascimento: prev.data_nascimento || (p.data_nascimento ? p.data_nascimento.split("T")[0] : ""),
            sexo: prev.sexo || p.sexo || "",
            endereco: prev.endereco || p.endereco || "",
            bairro: prev.bairro || p.bairro || "",
            cidade: prev.cidade || p.cidade || "",
            estado_civil: prev.estado_civil || p.estado_civil || "",
            situacao: prev.situacao || p.situacao || "",
            batismo: prev.batismo || p.batismo || "",
          }));
          setLookupState("found");
          toast.success(`Olá ${(p.nome_completo || "").split(" ")[0]}! Encontramos seu cadastro.`);
        } else {
          setPrefill({});
          setLookupState("notfound");
        }
      } catch (e) {
        setLookupState("idle");
      }
    }, 600);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.telefone]);

  useEffect(() => {
    // Se usuário estiver logado, redireciona para a página interna de inscrição
    if (user) {
      navigate(`/social/inscricao/${projectId}`);
      return;
    }
    
    loadProject();
  }, [projectId, user]);

  const loadProject = async () => {
    try {
      const response = await axios.get(`${API}/public/projects/${projectId}`);
      setProject(response.data.project);
      if (response.data.project?.campos_inscricao_externa?.includes("ministerios_desejados")) {
        axios.get(`${API}/ministries/public`).then(r => setMinistries(r.data || [])).catch(() => {});
      }
      setChurchInfo({
        nome: response.data.igreja_nome,
        logo: response.data.igreja_logo
      });
    } catch (error) {
      console.error("Error loading project:", error);
      toast.error("Projeto não encontrado ou não disponível para inscrição");
      navigate("/news");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.nome_completo.trim()) {
      toast.error("Nome é obrigatório");
      return;
    }
    if (!form.telefone.trim()) {
      toast.error("Telefone é obrigatório");
      return;
    }
    if (project?.valor_inscricao > 0 && !form.forma_pagamento) {
      toast.error("Selecione uma forma de pagamento");
      return;
    }

    setSubmitting(true);
    try {
      const always = ["nome_completo", "telefone", "observacoes", "forma_pagamento", "data_pagamento"];
      const payload = Object.fromEntries(Object.entries(form).filter(([k]) => always.includes(k) || has(k)));
      if (isInfantil) {
        payload.criancas = criancas.filter(c => c.nome.trim());
        if (payload.criancas.length === 0) {
          toast.error("Informe o nome de pelo menos uma criança");
          setSubmitting(false);
          return;
        }
      }
      const res = await axios.post(`${API}/public/projects/${projectId}/enroll`, payload);
      setAcesso(res.data?.acesso || null);
      setSuccess(true);
      toast.success("Inscrição realizada com sucesso!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao realizar inscrição");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-8 text-center">
            <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="h-12 w-12 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold mb-2">Inscrição Confirmada!</h2>
            <p className="text-muted-foreground mb-6">
              Sua inscrição em <strong>{project?.nome}</strong> foi realizada com sucesso.
            </p>
            {project?.valor_inscricao > 0 && (
              <div className="p-4 bg-amber-50 rounded-lg mb-6">
                <p className="text-sm text-amber-800">
                  Valor da inscrição: <strong>R$ {project.valor_inscricao.toFixed(2)}</strong>
                </p>
                <p className="text-xs text-amber-600 mt-1">
                  Entre em contato para confirmar o pagamento.
                </p>
              </div>
            )}
            {acesso && (
              <div className="p-4 rounded-xl border bg-blue-50 text-left mb-6" data-testid="enroll-access-box">
                <p className="text-sm font-semibold text-blue-900 flex items-center gap-2 mb-2"><KeyRound className="h-4 w-4" /> Seu acesso ao app foi criado</p>
                <p className="text-sm text-blue-900"><span className="text-blue-700">Usuário:</span> <strong data-testid="enroll-access-user">{acesso.usuario}</strong></p>
                <p className="text-sm text-blue-900"><span className="text-blue-700">Senha:</span> <strong data-testid="enroll-access-pass">{acesso.senha}</strong></p>
                <p className="text-xs text-blue-700 mt-2">Enviamos estes dados também no seu WhatsApp. Você pode alterar a senha no seu perfil.</p>
              </div>
            )}
            <div className="flex flex-col gap-2">
              {acesso && (
                <Button onClick={() => navigate("/login")} className="w-full" data-testid="enroll-go-login">
                  Entrar no app
                </Button>
              )}
              <Button variant={acesso ? "outline" : "default"} onClick={() => navigate("/news")} className="w-full">
                Voltar para a Página Inicial
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className={`min-h-screen bg-gradient-to-b ${theme.soft} via-white to-white`}>
      {/* Header */}
      <header className="bg-white/80 backdrop-blur border-b sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex items-center gap-3">
            {churchInfo.logo ? (
              <img src={churchInfo.logo} alt="" className="h-8 w-8 rounded-full object-cover" />
            ) : (
              <div className="h-8 w-8 rounded-full bg-primary flex items-center justify-center text-white font-bold">
                {(churchInfo.nome || "I").charAt(0)}
              </div>
            )}
            <span className="font-medium">{churchInfo.nome || "Igreja"}</span>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-3 sm:px-4 py-4 sm:py-6">
        {/* Project Info */}
        <Card className={`mb-4 sm:mb-6 overflow-hidden ring-1 ${theme.ring}`} data-testid="enroll-project-card">
          <div className={`h-1.5 ${theme.bg}`} />
          <CardHeader className="pb-3">
            <div className="flex items-center gap-3">
              <div className={`p-3 rounded-2xl ${theme.bg} text-white shadow-md shrink-0`}>
                <TypeIcon className="h-7 w-7" />
              </div>
              <div className="min-w-0">
                <p className={`text-xs font-medium ${theme.text}`}>{theme.label}</p>
                <CardTitle className="text-xl leading-tight break-words">{project?.nome}</CardTitle>
              </div>
            </div>
            {project?.objetivo && (
              <p className="text-sm text-muted-foreground pt-2">{project.objetivo}</p>
            )}
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex flex-wrap gap-4 text-sm">
              {project?.data_evento && (
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <span>{formatDateLong(project.data_evento)}</span>
                </div>
              )}
              {project?.horario_evento && (
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  <span>{project.horario_evento}</span>
                </div>
              )}
              {(project?.local_evento || project?.local) && (
                <div className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-muted-foreground" />
                  <span>{project.local_evento || project.local}</span>
                </div>
              )}
              {project?.dias_horarios && (
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  <span>{project.dias_horarios}</span>
                </div>
              )}
            </div>
            
            {project?.valor_inscricao > 0 && (
              <div className="p-3 bg-green-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <CreditCard className="h-4 w-4 text-green-600" />
                  <span className="font-semibold text-green-700">
                    Valor: R$ {project.valor_inscricao.toFixed(2)}
                  </span>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Enrollment Form */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Formulário de Inscrição</CardTitle>
            <p className="text-sm text-muted-foreground">
              Preencha seus dados para se inscrever
            </p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {isInfantil && (
                <p className={`text-sm font-semibold ${theme.text} flex items-center gap-2`}><User className="h-4 w-4" /> Dados do responsável</p>
              )}
              <div>
                <Label className="flex items-center gap-2">
                  <Phone className="h-4 w-4" />
                  Telefone (WhatsApp) *
                </Label>
                <Input
                  type="tel"
                  inputMode="tel"
                  value={form.telefone}
                  onChange={(e) => setForm({ ...form, telefone: e.target.value })}
                  placeholder="(00) 00000-0000"
                  required
                  autoFocus
                  data-testid="enroll-telefone"
                  className="h-12 text-base"
                />
                {lookupState === "loading" && <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" /> Verificando cadastro...</p>}
                {lookupState === "found" && <p className={`text-xs mt-1 flex items-center gap-1 ${theme.text}`} data-testid="enroll-lookup-found"><UserCheck className="h-3 w-3" /> Cadastro encontrado — confira seus dados abaixo</p>}
              </div>

              <div>
                <Label className="flex items-center gap-2">
                  <User className="h-4 w-4" />
                  Nome Completo *
                </Label>
                <Input
                  value={form.nome_completo}
                  onChange={(e) => setForm({ ...form, nome_completo: e.target.value })}
                  placeholder="Seu nome completo"
                  required
                  data-testid="enroll-nome"
                  className="h-12 text-base"
                />
              </div>

              {has("email") && (
                <div>
                  <Label className="flex items-center gap-2">
                    <Mail className="h-4 w-4" />
                    E-mail
                  </Label>
                  <Input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="seu@email.com"
                    data-testid="enroll-email"
                  />
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {has("data_nascimento") && (
                  <div>
                    <Label>Data de Nascimento</Label>
                    <Input type="date" value={form.data_nascimento} onChange={(e) => setForm({ ...form, data_nascimento: e.target.value })} data-testid="enroll-data-nascimento" />
                  </div>
                )}
                {has("sexo") && (
                  <div>
                    <Label>Sexo</Label>
                    <Select value={form.sexo} onValueChange={(v) => setForm({ ...form, sexo: v })}>
                      <SelectTrigger data-testid="enroll-sexo"><SelectValue placeholder="Selecione..." /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Masculino">Masculino</SelectItem>
                        <SelectItem value="Feminino">Feminino</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {has("estado_civil") && (
                  <div>
                    <Label>Estado Civil</Label>
                    <Select value={form.estado_civil} onValueChange={(v) => setForm({ ...form, estado_civil: v })}>
                      <SelectTrigger data-testid="enroll-estado-civil"><SelectValue placeholder="Selecione..." /></SelectTrigger>
                      <SelectContent>
                        {["Solteiro(a)", "Casado(a)", "Divorciado(a)", "Viúvo(a)", "União Estável"].map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {has("situacao") && (
                  <div>
                    <Label>Situação</Label>
                    <Select value={form.situacao} onValueChange={(v) => setForm({ ...form, situacao: v })}>
                      <SelectTrigger data-testid="enroll-situacao"><SelectValue placeholder="Selecione..." /></SelectTrigger>
                      <SelectContent>
                        {["Membro", "Frequentador", "Visitante"].map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {has("batismo") && (
                  <div>
                    <Label>Batismo</Label>
                    <Select value={form.batismo} onValueChange={(v) => setForm({ ...form, batismo: v })}>
                      <SelectTrigger data-testid="enroll-batismo"><SelectValue placeholder="Selecione..." /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Sim">Sim, sou batizado(a)</SelectItem>
                        <SelectItem value="Não">Ainda não</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {has("ministerios_desejados") && (
                  <div>
                    <Label>Ministério de interesse</Label>
                    <Select value={form.ministerios_desejados} onValueChange={(v) => setForm({ ...form, ministerios_desejados: v })}>
                      <SelectTrigger data-testid="enroll-ministerio"><SelectValue placeholder="Selecione..." /></SelectTrigger>
                      <SelectContent>
                        {ministries.map(m => <SelectItem key={m.id} value={m.id}>{m.nome}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>

              {has("endereco") && (
                <div>
                  <Label>Endereço</Label>
                  <Input value={form.endereco} onChange={(e) => setForm({ ...form, endereco: e.target.value })} placeholder="Rua, número, complemento" data-testid="enroll-endereco" />
                </div>
              )}

              {(has("bairro") || has("cidade")) && (
                <div className="grid grid-cols-2 gap-4">
                  {has("bairro") && (
                    <div>
                      <Label>Bairro</Label>
                      <Input value={form.bairro} onChange={(e) => setForm({ ...form, bairro: e.target.value })} data-testid="enroll-bairro" />
                    </div>
                  )}
                  {has("cidade") && (
                    <div>
                      <Label>Cidade</Label>
                      <Input value={form.cidade} onChange={(e) => setForm({ ...form, cidade: e.target.value })} data-testid="enroll-cidade" />
                    </div>
                  )}
                </div>
              )}

              {(has("convidado_nome") || has("convidado_telefone") || has("convidado_email")) && (
                <div className="p-4 border rounded-lg bg-pink-50/50 space-y-3">
                  <p className="text-sm font-medium text-pink-700">Dados do Convidado</p>
                  {has("convidado_nome") && (
                    <div>
                      <Label>Nome do Convidado</Label>
                      <Input value={form.convidado_nome} onChange={(e) => setForm({ ...form, convidado_nome: e.target.value })} data-testid="enroll-convidado-nome" />
                    </div>
                  )}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {has("convidado_telefone") && (
                      <div>
                        <Label>Telefone do Convidado</Label>
                        <Input value={form.convidado_telefone} onChange={(e) => setForm({ ...form, convidado_telefone: e.target.value })} placeholder="(00) 00000-0000" data-testid="enroll-convidado-telefone" />
                      </div>
                    )}
                    {has("convidado_email") && (
                      <div>
                        <Label>E-mail do Convidado</Label>
                        <Input type="email" value={form.convidado_email} onChange={(e) => setForm({ ...form, convidado_email: e.target.value })} data-testid="enroll-convidado-email" />
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Campos de Pagamento - apenas para eventos pagos */}
              {project?.valor_inscricao > 0 && (
                <div className="p-4 border rounded-lg bg-amber-50/50 space-y-4">
                  <h4 className="font-medium flex items-center gap-2">
                    <CreditCard className="h-4 w-4" />
                    Informações de Pagamento
                  </h4>
                  
                  {/* PIX Info */}
                  {project.pix_chave && (
                    <div className="p-4 bg-white rounded-lg border-2 border-dashed border-green-300">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-green-800">Chave PIX:</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-8 px-2 text-green-700 hover:text-green-900"
                          onClick={() => {
                            navigator.clipboard.writeText(project.pix_chave);
                            toast.success("Chave PIX copiada!");
                          }}
                        >
                          <Copy className="h-4 w-4 mr-1" />
                          Copiar
                        </Button>
                      </div>
                      <div className="p-3 bg-green-50 rounded-lg font-mono text-sm break-all">
                        {project.pix_chave}
                      </div>
                      {project.pix_titular && (
                        <p className="text-xs text-muted-foreground mt-2">
                          Titular: <strong>{project.pix_titular}</strong>
                        </p>
                      )}
                      <div className="mt-3 p-2 bg-amber-100 rounded text-xs text-amber-800">
                        Valor: <strong>R$ {project.valor_inscricao.toFixed(2)}</strong>
                      </div>
                    </div>
                  )}

                  <div>
                    <Label>Forma de Pagamento *</Label>
                    <Select 
                      value={form.forma_pagamento} 
                      onValueChange={(v) => setForm({ ...form, forma_pagamento: v })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione..." />
                      </SelectTrigger>
                      <SelectContent>
                        {FORMAS_PAGAMENTO.map(fp => (
                          <SelectItem key={fp.value} value={fp.value}>{fp.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Data do Pagamento</Label>
                    <Input
                      type="date"
                      value={form.data_pagamento}
                      onChange={(e) => setForm({ ...form, data_pagamento: e.target.value })}
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Preencha se já realizou o pagamento
                    </p>
                  </div>
                </div>
              )}

              <div>
                <Label>Observações</Label>
                <Textarea
                  value={form.observacoes}
                  onChange={(e) => setForm({ ...form, observacoes: e.target.value })}
                  placeholder="Alguma informação adicional..."
                  rows={3}
                />
              </div>

              {isInfantil && (
                <div className={`p-4 rounded-xl border ring-1 ${theme.ring} bg-yellow-50/40 space-y-3`} data-testid="enroll-children-box">
                  <p className={`text-sm font-semibold ${theme.text} flex items-center gap-2`}><Baby className="h-4 w-4" /> Crianças que vão participar</p>
                  {criancas.map((c, i) => (
                    <div key={i} className="flex gap-2 items-end">
                      <div className="flex-1 min-w-0">
                        <Label className="text-xs">Nome da criança *</Label>
                        <Input value={c.nome} onChange={(e) => setCriancas(prev => prev.map((x, j) => j === i ? { ...x, nome: e.target.value } : x))} placeholder="Nome completo" className="h-11" data-testid={`enroll-child-name-${i}`} />
                      </div>
                      <div className="w-[84px] shrink-0">
                <Label className="text-xs">Idade</Label>
                <Input type="number" inputMode="numeric" min={0} max={17} placeholder="anos" value={c.idade} onChange={(e) => setCriancas(prev => prev.map((x, j) => j === i ? { ...x, idade: e.target.value } : x))} className="h-11 min-w-0" data-testid={`enroll-child-age-${i}`} />
              </div>
                      {criancas.length > 1 && (
                        <Button type="button" variant="ghost" size="icon" className="h-11 w-9 text-red-500 shrink-0" onClick={() => setCriancas(prev => prev.filter((_, j) => j !== i))}><X className="h-4 w-4" /></Button>
                      )}
                    </div>
                  ))}
                  <Button type="button" variant="outline" size="sm" className="w-full" onClick={() => setCriancas(prev => [...prev, { nome: "", idade: "" }])} data-testid="enroll-child-add">
                    <Plus className="h-4 w-4 mr-1" /> Adicionar outra criança
                  </Button>
                </div>
              )}

              <Button type="submit" className={`w-full h-12 text-base ${theme.bg} hover:opacity-90 text-white`} disabled={submitting} data-testid="enroll-submit">
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Enviando...
                  </>
                ) : (
                  "Confirmar Inscrição"
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Already have account? */}
        <div className="mt-6 text-center">
          <p className="text-sm text-muted-foreground">
            Já tem uma conta?{" "}
            <Button 
              variant="link" 
              className="p-0 h-auto" 
              onClick={() => navigate(`/login?redirect=/social/inscricao/${projectId}`)}
            >
              Faça login
            </Button>
          </p>
        </div>
      </main>
    </div>
  );
}
